import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Banknote, Upload, Loader2, RefreshCw, Save, Trash2, ArrowDownCircle, ArrowUpCircle, Tag, FileText, TrendingDown, TrendingUp, AlertTriangle, Target, Lock, CheckCircle2, Circle, Calendar, Receipt, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHARGE_ORDER = [
  'comptable',
  'forfait_cb',
  'assurance_pret_homme_cle',
  'assurance_pret_kereis',
  'decennale',
  'frais_km',
  'emprunt',
  'salaire_urssaf',
];

const fmtEUR = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SuiviBancairePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [draftCharges, setDraftCharges] = useState({});
  const [draftCustomCharges, setDraftCustomCharges] = useState([]);
  const [draftTreso, setDraftTreso] = useState(0);
  const [draftObjectifs, setDraftObjectifs] = useState({ ca_annuel: 0, benefice_annuel: 0, seuil_tresorerie: 0, date_debut_activite: '' });
  const [kpi, setKpi] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [releves, setReleves] = useState([]);
  const [selectedReleve, setSelectedReleve] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  // P1c: Vue mensuelle exercice + TVA (Loutre d59c9bfe)
  const [exerciceYear, setExerciceYear] = useState(new Date().getFullYear());
  const [exercice, setExercice] = useState(null);
  const [moisManquants, setMoisManquants] = useState([]);
  const [uploadingTvaMonth, setUploadingTvaMonth] = useState(null);
  const [uploadingReleveMonth, setUploadingReleveMonth] = useState(null);
  const [duplicatesCount, setDuplicatesCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [cf, rl, kp] = await Promise.all([
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/charges-fixes`),
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/releves`),
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/kpi`),
      ]);
      setData(cf.data);
      // Normalize charges: backend now returns {key: {montant, date_debut}}
      const nc = {};
      Object.entries(cf.data.charges || {}).forEach(([k, v]) => {
        nc[k] = typeof v === 'object' && v !== null
          ? { montant: v.montant ?? 0, date_debut: v.date_debut || '' }
          : { montant: v ?? 0, date_debut: '' };
      });
      setDraftCharges(nc);
      setDraftCustomCharges(
        (cf.data.custom_charges || []).map((c) => ({
          id: c.id,
          label: c.label || '',
          montant: c.montant ?? 0,
          date_debut: c.date_debut || '',
        }))
      );
      setDraftTreso(cf.data.tresorerie_actuelle || 0);
      setDraftObjectifs({
        ca_annuel: cf.data.objectifs?.ca_annuel || 0,
        benefice_annuel: cf.data.objectifs?.benefice_annuel || 0,
        seuil_tresorerie: cf.data.objectifs?.seuil_tresorerie || 0,
        date_debut_activite: cf.data.objectifs?.date_debut_activite || '',
      });
      setReleves(rl.data.releves || []);
      setKpi(kp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // P1c: load monthly exercice view + missing month alerts
  const loadExercice = async (year = exerciceYear) => {
    try {
      const [ex, al, dc] = await Promise.all([
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/exercice/${year}`),
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/alerte-mois-manquants`),
        axios.get(`${BACKEND}/api/admin/suivi-bancaire/releves-duplicates-count`),
      ]);
      setExercice(ex.data);
      setMoisManquants(al.data.mois_manquants || []);
      setDuplicatesCount(dc.data.doublons || 0);
    } catch (e) {
      // silent (page still usable without this view)
    }
  };
  useEffect(() => { loadExercice(exerciceYear); }, [exerciceYear]);

  const handleDedupeReleves = async () => {
    if (!window.confirm(`Supprimer ${duplicatesCount} relevé(s) en double ? Le plus récent de chaque groupe sera conservé.`)) return;
    try {
      const res = await axios.post(`${BACKEND}/api/admin/suivi-bancaire/releves-dedupe`);
      toast.success(`✅ ${res.data.supprimes} doublon(s) supprimé(s). ${res.data.restants} relevé(s) unique(s) restant(s).`);
      await Promise.all([load(), loadExercice()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression échouée');
    }
  };

  const handleUploadReleveForMonth = async (file, mois) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Le fichier doit être un PDF');
      return;
    }
    setUploadingReleveMonth(mois);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND}/api/admin/suivi-bancaire/upload-releve`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const releveId = res.data.releve_id;
      toast.info('📤 Relevé reçu — analyse IA en arrière-plan…');

      // Poll for completion
      const maxTries = 40;
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const r = await axios.get(`${BACKEND}/api/admin/suivi-bancaire/releves/${releveId}`);
          if (r.data.status === 'done') {
            toast.success(`✅ ${(r.data.transactions || []).length} transactions extraites`);
            setUploadingReleveMonth(null);
            await Promise.all([load(), loadExercice()]);
            return;
          }
          if (r.data.status === 'error') {
            toast.error(`Erreur IA : ${r.data.error_message || 'inconnue'}`);
            setUploadingReleveMonth(null);
            await loadExercice();
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries < maxTries) setTimeout(poll, 3000);
        else {
          setUploadingReleveMonth(null);
          toast.warning('Analyse IA plus longue que prévu — actualise dans quelques instants');
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
      setUploadingReleveMonth(null);
    }
  };

  const handleUploadTvaForMonth = async (file, mois, annee) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Le fichier doit être un PDF');
      return;
    }
    setUploadingTvaMonth(mois);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (mois) fd.append('mois', String(mois));
      if (annee) fd.append('annee', String(annee));
      const res = await axios.post(`${BACKEND}/api/admin/suivi-bancaire/upload-tva`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const declId = res.data.declaration_id;
      toast.info('📤 Déclaration TVA reçue — analyse IA…');

      const maxTries = 40;
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const r = await axios.get(`${BACKEND}/api/admin/suivi-bancaire/declarations-tva/${declId}`);
          if (r.data.status === 'done') {
            toast.success(`✅ TVA extraite — collectée ${r.data.tva_collectee}€ / déductible ${r.data.tva_deductible}€`);
            setUploadingTvaMonth(null);
            await loadExercice();
            return;
          }
          if (r.data.status === 'error') {
            toast.error(`Erreur IA : ${r.data.error_message || 'inconnue'}`);
            setUploadingTvaMonth(null);
            await loadExercice();
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries < maxTries) setTimeout(poll, 3000);
        else {
          setUploadingTvaMonth(null);
          toast.warning('Analyse IA plus longue que prévu');
        }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload TVA");
      setUploadingTvaMonth(null);
    }
  };

  const handleDeleteTva = async (declId) => {
    if (!window.confirm('Supprimer cette déclaration TVA ?')) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/suivi-bancaire/declarations-tva/${declId}`);
      toast.success('Déclaration TVA supprimée');
      await loadExercice();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression échouée');
    }
  };

  const downloadFile = async (url, defaultFilename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      // Try to extract filename from Content-Disposition
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = (m && m[1]) || defaultFilename;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Téléchargement échoué');
    }
  };
  const downloadReleve = (id) => downloadFile(`${BACKEND}/api/admin/suivi-bancaire/releves/${id}/download`, `releve.pdf`);
  const downloadTva = (id) => downloadFile(`${BACKEND}/api/admin/suivi-bancaire/declarations-tva/${id}/download`, `declaration-tva.pdf`);

  const saveCharges = async () => {
    setSaving(true);
    try {
      // Send charges as {key: {montant, date_debut}}
      const chargesPayload = {};
      Object.entries(draftCharges).forEach(([k, v]) => {
        chargesPayload[k] = {
          montant: parseFloat(v.montant) || 0,
          date_debut: v.date_debut || null,
        };
      });
      await axios.put(`${BACKEND}/api/admin/suivi-bancaire/charges-fixes`, {
        charges: chargesPayload,
        custom_charges: draftCustomCharges
          .filter((c) => (c.label || '').trim() !== '')
          .map((c) => ({
            id: c.id,
            label: c.label.trim(),
            montant: parseFloat(c.montant) || 0,
            date_debut: c.date_debut || null,
          })),
        tresorerie_actuelle: parseFloat(draftTreso) || 0,
        objectifs: {
          ca_annuel: parseFloat(draftObjectifs.ca_annuel) || 0,
          benefice_annuel: parseFloat(draftObjectifs.benefice_annuel) || 0,
          seuil_tresorerie: parseFloat(draftObjectifs.seuil_tresorerie) || 0,
          date_debut_activite: draftObjectifs.date_debut_activite || null,
        },
      });
      toast.success('Enregistré');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Le fichier doit être un PDF');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${BACKEND}/api/admin/suivi-bancaire/upload-releve`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
      );
      const releveId = res.data.releve_id;
      toast.info('📤 PDF reçu — analyse IA en arrière-plan…');

      // Polling for completion (max 3 minutes)
      const maxTries = 60;
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const r = await axios.get(`${BACKEND}/api/admin/suivi-bancaire/releves/${releveId}`);
          const st = r.data.status;
          if (st === 'done') {
            const txns = r.data.transactions || [];
            const tot_c = txns.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
            const tot_d = txns.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);
            toast.success(`✅ ${txns.length} transactions extraites — +${fmtEUR(tot_c)} / ${fmtEUR(tot_d)}`);
            setSelectedReleve(r.data);
            await load();
            setUploading(false);
            return;
          }
          if (st === 'error') {
            toast.error(`Échec de l'analyse : ${r.data.error_message || 'erreur inconnue'}`);
            setUploading(false);
            await load();
            return;
          }
          if (tries >= maxTries) {
            toast.warning('Analyse encore en cours — rafraîchissez la page dans 1-2 minutes pour voir le résultat.');
            setUploading(false);
            await load();
            return;
          }
          setTimeout(poll, 3000);
        } catch (e) {
          if (tries >= maxTries) {
            toast.error('Polling échoué — vérifiez votre connexion');
            setUploading(false);
            return;
          }
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l&apos;upload');
      setUploading(false);
    }
  };

  const openReleve = async (id) => {
    try {
      const res = await axios.get(`${BACKEND}/api/admin/suivi-bancaire/releves/${id}`);
      setSelectedReleve(res.data);
      setFilterCat('all');
    } catch (e) {
      toast.error('Impossible de charger le relevé');
    }
  };

  const deleteReleve = async (id) => {
    if (!window.confirm('Supprimer ce relevé ?')) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/suivi-bancaire/releves/${id}`);
      toast.success('Relevé supprimé');
      setSelectedReleve(null);
      await load();
    } catch (e) {
      toast.error('Suppression échouée');
    }
  };

  const filteredTxns = (selectedReleve?.transactions || []).filter((t) => {
    if (filterCat === 'all') return true;
    if (filterCat === 'debit') return t.amount < 0 && !t.charge_category;
    if (filterCat === 'credit') return t.amount > 0;
    if (filterCat === 'frais_fixes') return !!t.charge_category;
    return true;
  });

  const totalMensuel =
    Object.values(draftCharges).reduce((s, v) => s + (parseFloat(v?.montant) || 0), 0) +
    draftCustomCharges.reduce((s, c) => s + (parseFloat(c?.montant) || 0), 0);
  const totalAnnuel = totalMensuel * 12;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Banknote className="w-6 h-6 text-emerald-600" />
          Suivi bancaire
        </h1>
        <button onClick={load} className="h-9 px-3 bg-white border border-slate-300 rounded-lg flex items-center gap-2 text-xs hover:bg-slate-50" data-testid="suivi-refresh">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* === Alerte trésorerie === */}
          {kpi?.tresorerie_alerte && (
            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-center gap-3 animate-pulse" data-testid="alerte-tresorerie">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-red-900">Alerte trésorerie basse</p>
                <p className="text-xs text-red-700">
                  Solde actuel <span className="font-bold">{fmtEUR(kpi.tresorerie)}</span> sous le seuil <span className="font-bold">{fmtEUR(kpi.seuil_tresorerie)}</span>
                </p>
              </div>
            </div>
          )}

          {/* === Widgets objectifs annuels === */}
          {kpi && (kpi.ca_obj_annuel > 0 || kpi.benef_obj_annuel > 0) && (
            <div className="grid md:grid-cols-3 gap-4" data-testid="widgets-objectifs">
              {/* CA prévisionnel */}
              {kpi.ca_obj_annuel > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">Objectif CA annuel</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900">{fmtEUR(kpi.ca_realise)} <span className="text-sm font-normal text-slate-500">/ {fmtEUR(kpi.ca_obj_annuel)}</span></p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    À ce jour, tu devrais être à {fmtEUR(kpi.ca_obj_prorata)} ({kpi.days_elapsed}/{kpi.days_total} j)
                  </p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                    {kpi.days_total > 0 && (
                      <div
                        className="absolute top-0 h-full w-0.5 bg-slate-500 z-10"
                        style={{ left: `${Math.min(100, (kpi.days_elapsed / kpi.days_total) * 100)}%` }}
                        title="Position attendue au prorata temporis"
                      />
                    )}
                    <div
                      className={`h-full rounded-full ${(kpi.ca_realise / kpi.ca_obj_annuel) * 100 >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (kpi.ca_realise / kpi.ca_obj_annuel) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs font-semibold mt-1 text-blue-600">
                    {((kpi.ca_realise / kpi.ca_obj_annuel) * 100).toFixed(1)}% de l'objectif annuel
                    {kpi.ca_obj_prorata > 0 && (
                      <span className={kpi.ca_realise >= kpi.ca_obj_prorata ? 'text-green-600 ml-1' : 'text-amber-600 ml-1'}>
                        · {kpi.ca_realise >= kpi.ca_obj_prorata ? '↑ en avance' : '↓ en retard'} de {fmtEUR(Math.abs(kpi.ca_realise - kpi.ca_obj_prorata))}
                      </span>
                    )}
                  </p>
                  {kpi.days_elapsed > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      À ce rythme, fin d'année : <span className="font-semibold text-slate-900">{fmtEUR((kpi.ca_realise / kpi.days_elapsed) * kpi.days_total)}</span>
                    </p>
                  )}
                </div>
              )}
              {/* Bénéfice prévisionnel */}
              {kpi.benef_obj_annuel > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">Objectif Bénéfice</span>
                  </div>
                  <p className={`text-xl font-bold ${kpi.benef_realise >= 0 ? 'text-slate-900' : 'text-red-700'}`}>{fmtEUR(kpi.benef_realise)} <span className="text-sm font-normal text-slate-500">/ {fmtEUR(kpi.benef_obj_annuel)}</span></p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Attendu à ce jour : {fmtEUR(kpi.benef_obj_prorata)} · Charges cumul. {fmtEUR(kpi.charges_cumulees)}
                  </p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                    {kpi.days_total > 0 && (
                      <div
                        className="absolute top-0 h-full w-0.5 bg-slate-500 z-10"
                        style={{ left: `${Math.min(100, (kpi.days_elapsed / kpi.days_total) * 100)}%` }}
                      />
                    )}
                    <div
                      className={`h-full rounded-full ${(kpi.benef_realise / kpi.benef_obj_annuel) * 100 >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.max(0, Math.min(100, (kpi.benef_realise / kpi.benef_obj_annuel) * 100))}%` }}
                    />
                  </div>
                  <p className="text-xs font-semibold mt-1 text-blue-600">
                    {((kpi.benef_realise / kpi.benef_obj_annuel) * 100).toFixed(1)}% de l'objectif annuel
                    {kpi.benef_obj_prorata > 0 && (
                      <span className={kpi.benef_realise >= kpi.benef_obj_prorata ? 'text-green-600 ml-1' : 'text-amber-600 ml-1'}>
                        · {kpi.benef_realise >= kpi.benef_obj_prorata ? '↑ en avance' : '↓ en retard'} de {fmtEUR(Math.abs(kpi.benef_realise - kpi.benef_obj_prorata))}
                      </span>
                    )}
                  </p>
                  {kpi.days_elapsed > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      À ce rythme, fin d'année : <span className="font-semibold text-slate-900">{fmtEUR((kpi.benef_realise / kpi.days_elapsed) * kpi.days_total)}</span>
                    </p>
                  )}
                </div>
              )}
              {/* Point mort */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-semibold text-slate-600 uppercase">Point mort atteint</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{fmtEUR(kpi.point_mort_annuel)}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Charges annuelles à couvrir · {fmtEUR(kpi.total_mensuel_actif)}/mois
                </p>
                <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${kpi.point_mort_atteint ? 'bg-green-500' : 'bg-amber-500'}`}
                    style={{ width: `${kpi.point_mort_annuel > 0 ? Math.min(100, (kpi.ca_realise / kpi.point_mort_annuel) * 100) : 0}%` }}
                  />
                </div>
                {kpi.point_mort_atteint ? (
                  <p className="text-xs font-semibold mt-1 text-green-600">
                    ✓ Atteint · CA {fmtEUR(kpi.ca_realise)} · +{fmtEUR(kpi.ca_realise - kpi.point_mort_annuel)} au-delà
                  </p>
                ) : (
                  <p className="text-xs font-semibold mt-1 text-amber-600">
                    CA {fmtEUR(kpi.ca_realise)} · reste {fmtEUR(kpi.point_mort_annuel - kpi.ca_realise)} pour atteindre le seuil
                  </p>
                )}
              </div>
            </div>
          )}

          {/* === Charges fixes & point mort === */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              Charges fixes & point mort
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_120px_130px] gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                  <span>Charge</span>
                  <span className="text-right">€/mois</span>
                  <span className="text-right">Date début</span>
                </div>
                {CHARGE_ORDER.map((key) => {
                  const c = draftCharges[key] || { montant: 0, date_debut: '' };
                  return (
                    <div key={key} className="grid grid-cols-[1fr_120px_130px] gap-2 items-center">
                      <label className="text-sm text-slate-700 truncate" title={data.labels[key]}>{data.labels[key]}</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          value={c.montant}
                          onChange={(e) => setDraftCharges((p) => ({ ...p, [key]: { ...p[key], montant: e.target.value } }))}
                          className="w-full h-9 px-2 pr-6 border border-slate-300 rounded-md text-right text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                          data-testid={`charge-${key}-input`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                      </div>
                      <input
                        type="date"
                        value={c.date_debut || ''}
                        onChange={(e) => setDraftCharges((p) => ({ ...p, [key]: { ...p[key], date_debut: e.target.value } }))}
                        className="h-9 px-2 border border-slate-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                        data-testid={`charge-${key}-date`}
                        title="Date d'entrée en vigueur — les charges à date future ne sont pas comptées dans le point mort"
                      />
                    </div>
                  );
                })}

                {/* Custom charges (P1b Loutre 1e0e5b42 - lignes dynamiques ajoutées par l'utilisateur) */}
                {draftCustomCharges.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-dashed border-slate-200">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-1 mb-1">Charges personnalisées</p>
                  </div>
                )}
                {draftCustomCharges.map((c, idx) => (
                  <div key={c.id} className="grid grid-cols-[1fr_120px_130px_auto] gap-2 items-center" data-testid={`custom-charge-row-${idx}`}>
                    <input
                      type="text"
                      value={c.label}
                      placeholder="Nom de la charge…"
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraftCustomCharges((prev) => prev.map((x, i) => (i === idx ? { ...x, label: v } : x)));
                      }}
                      className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                      data-testid={`custom-charge-${idx}-label`}
                    />
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={c.montant}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftCustomCharges((prev) => prev.map((x, i) => (i === idx ? { ...x, montant: v } : x)));
                        }}
                        className="w-full h-9 px-2 pr-6 border border-slate-300 rounded-md text-right text-sm focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                        data-testid={`custom-charge-${idx}-input`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                    </div>
                    <input
                      type="date"
                      value={c.date_debut || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDraftCustomCharges((prev) => prev.map((x, i) => (i === idx ? { ...x, date_debut: v } : x)));
                      }}
                      className="h-9 px-2 border border-slate-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                      data-testid={`custom-charge-${idx}-date`}
                      title="Date d'entrée en vigueur — les charges à date future ne sont pas comptées dans le point mort"
                    />
                    <button
                      type="button"
                      onClick={() => setDraftCustomCharges((prev) => prev.filter((_, i) => i !== idx))}
                      className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                      title="Supprimer cette charge"
                      data-testid={`custom-charge-${idx}-delete`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() =>
                    setDraftCustomCharges((prev) => [
                      ...prev,
                      {
                        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        label: '',
                        montant: 0,
                        date_debut: '',
                      },
                    ])
                  }
                  className="mt-2 w-full h-9 border-2 border-dashed border-slate-300 rounded-md text-xs font-medium text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/40 transition-colors"
                  data-testid="add-custom-charge-btn"
                >
                  + Ajouter une charge
                </button>
              </div>

              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-slate-500">Total mensuel (point mort)</span>
                    <span className="text-2xl font-bold text-slate-800" data-testid="total-mensuel">{fmtEUR(totalMensuel)}</span>
                  </div>
                  <div className="flex justify-between items-baseline mt-2">
                    <span className="text-xs text-slate-500">Total annuel (×12)</span>
                    <span className="text-lg font-semibold text-slate-700" data-testid="total-annuel">{fmtEUR(totalAnnuel)}</span>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <label className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Trésorerie actuelle</label>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      step="0.01"
                      value={draftTreso}
                      onChange={(e) => setDraftTreso(e.target.value)}
                      className="w-full h-11 px-3 pr-8 border border-emerald-200 rounded-md text-right text-xl font-bold focus:ring-2 focus:ring-emerald-300 focus:outline-none"
                      data-testid="tresorerie-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-600">€</span>
                  </div>
                  <p className="text-[11px] text-emerald-700/80 italic mt-2">
                    Estimation fin d&apos;année (auto) : <span className="font-bold">{fmtEUR(data.estimation_fin_annee)}</span>
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide flex items-center gap-1">
                    <Target className="w-3.5 h-3.5" />
                    Objectifs annuels & seuil
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-blue-700 font-medium">
                      Date début activité
                      <input
                        type="date"
                        value={draftObjectifs.date_debut_activite}
                        onChange={(e) => setDraftObjectifs((p) => ({ ...p, date_debut_activite: e.target.value }))}
                        className="mt-0.5 w-full h-8 px-2 border border-blue-200 rounded text-xs focus:ring-2 focus:ring-blue-300 focus:outline-none"
                        data-testid="date-debut-activite"
                      />
                    </label>
                    <label className="text-[11px] text-blue-700 font-medium">
                      Objectif CA annuel (€)
                      <input
                        type="number"
                        step="1000"
                        value={draftObjectifs.ca_annuel}
                        onChange={(e) => setDraftObjectifs((p) => ({ ...p, ca_annuel: e.target.value }))}
                        className="mt-0.5 w-full h-8 px-2 border border-blue-200 rounded text-xs text-right focus:ring-2 focus:ring-blue-300 focus:outline-none"
                        data-testid="obj-ca-annuel"
                      />
                    </label>
                    <label className="text-[11px] text-blue-700 font-medium">
                      Objectif bénéfice (€)
                      <input
                        type="number"
                        step="1000"
                        value={draftObjectifs.benefice_annuel}
                        onChange={(e) => setDraftObjectifs((p) => ({ ...p, benefice_annuel: e.target.value }))}
                        className="mt-0.5 w-full h-8 px-2 border border-blue-200 rounded text-xs text-right focus:ring-2 focus:ring-blue-300 focus:outline-none"
                        data-testid="obj-benefice-annuel"
                      />
                    </label>
                    <label className="text-[11px] text-blue-700 font-medium">
                      Seuil alerte trésorerie (€)
                      <input
                        type="number"
                        step="1000"
                        value={draftObjectifs.seuil_tresorerie}
                        onChange={(e) => setDraftObjectifs((p) => ({ ...p, seuil_tresorerie: e.target.value }))}
                        className="mt-0.5 w-full h-8 px-2 border border-blue-200 rounded text-xs text-right focus:ring-2 focus:ring-blue-300 focus:outline-none"
                        data-testid="seuil-tresorerie"
                      />
                    </label>
                  </div>
                </div>

                <button
                  onClick={saveCharges}
                  disabled={saving}
                  className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md flex items-center justify-center gap-2 text-sm font-medium"
                  data-testid="save-charges-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>

          {/* === Vue mensuelle exercice (P1c Loutre d59c9bfe) === */}
          {exercice && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="vue-mensuelle">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-500" />
                  Vue mensuelle — Exercice {exerciceYear}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {duplicatesCount > 0 && (
                    <button
                      onClick={handleDedupeReleves}
                      className="h-8 px-2.5 text-[11px] bg-amber-50 border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 flex items-center gap-1"
                      data-testid="dedupe-releves-btn"
                      title="Supprimer les relevés en double (garde le plus récent)"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" /> Supprimer {duplicatesCount} doublon{duplicatesCount > 1 ? 's' : ''}
                    </button>
                  )}
                  <label className="text-xs text-slate-500">Année :</label>
                  <select
                    value={exerciceYear}
                    onChange={(e) => setExerciceYear(parseInt(e.target.value))}
                    className="h-8 px-2 border border-slate-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                    data-testid="exercice-year-select"
                  >
                    {[new Date().getFullYear() + 1, new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Alerte mois manquants */}
              {moisManquants.length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="alerte-mois-manquants">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-xs">
                    <p className="font-semibold text-amber-900">Relevés bancaires manquants :</p>
                    <p className="text-amber-800 mt-0.5">
                      {moisManquants.map((m) => ['Janv.', 'Févr.', 'Mars', 'Avril', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'][m - 1]).join(', ')} — pense à les uploader pour à jour ton exercice.
                    </p>
                  </div>
                </div>
              )}

              {/* Récap exercice */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="recap-exercice">
                <RecapCard label="Total crédits" value={exercice.recap.total_credits} color="text-emerald-700" bg="bg-emerald-50 border-emerald-200" />
                <RecapCard label="Total débits" value={exercice.recap.total_debits} color="text-red-700" bg="bg-red-50 border-red-200" />
                <RecapCard label="TVA collectée" value={exercice.recap.total_tva_collectee} color="text-blue-700" bg="bg-blue-50 border-blue-200" />
                <RecapCard label="TVA déductible" value={exercice.recap.total_tva_deductible} color="text-indigo-700" bg="bg-indigo-50 border-indigo-200" />
                <RecapCard label="TVA à payer" value={exercice.recap.total_tva_a_payer} color="text-orange-700" bg="bg-orange-50 border-orange-200" />
                <RecapCard label="Crédits HT" value={exercice.recap.total_credits_ht} color="text-emerald-800" bg="bg-emerald-100 border-emerald-300" tooltip="Crédits − TVA collectée" />
              </div>

              {/* Tableau 12 mois */}
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs" data-testid="tableau-mensuel">
                  <thead>
                    <tr className="text-slate-500 uppercase text-[10px] border-b border-slate-200">
                      <th className="text-left py-2 pr-2">Mois</th>
                      <th className="text-center py-2 px-1">Relevé</th>
                      <th className="text-right py-2 px-1">Crédits</th>
                      <th className="text-right py-2 px-1">Débits</th>
                      <th className="text-center py-2 px-1">TVA</th>
                      <th className="text-right py-2 px-1">TVA coll.</th>
                      <th className="text-right py-2 px-1">TVA déd.</th>
                      <th className="text-right py-2 px-1">Crédits HT</th>
                      <th className="text-right py-2 px-1">Cumul créd.</th>
                      <th className="text-right py-2 px-1">Cumul déb.</th>
                      <th className="text-center py-2 pl-1">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exercice.rows.map((row) => (
                      <tr key={row.mois} className={`border-b border-slate-100 hover:bg-slate-50 ${row.verrouille ? 'opacity-70' : ''}`} data-testid={`mois-row-${row.mois}`}>
                        <td className="py-2 pr-2 font-medium text-slate-800 flex items-center gap-1">
                          {row.verrouille && <Lock className="w-3 h-3 text-slate-400" title="Mois verrouillé (clôturé)" />}
                          {row.mois_label}
                        </td>
                        <td className="text-center py-2 px-1">
                          <StatusBadge status={row.releve_status} label={row.releves.length > 0 ? `${row.releves.length}` : ''} loading={uploadingReleveMonth === row.mois} />
                        </td>
                        <td className={`text-right py-2 px-1 ${row.credits > 0 ? 'text-emerald-700 font-medium' : 'text-slate-400'}`}>
                          {row.credits > 0 ? fmtEUR(row.credits) : '—'}
                        </td>
                        <td className={`text-right py-2 px-1 ${row.debits < 0 ? 'text-red-700 font-medium' : 'text-slate-400'}`}>
                          {row.debits < 0 ? fmtEUR(row.debits) : '—'}
                        </td>
                        <td className="text-center py-2 px-1">
                          <StatusBadge status={row.tva_status} label={row.tva_declarations.length > 0 ? '✓' : ''} loading={uploadingTvaMonth === row.mois} />
                        </td>
                        <td className={`text-right py-2 px-1 ${row.tva_collectee > 0 ? 'text-blue-700 font-medium' : 'text-slate-400'}`}>
                          {row.tva_collectee > 0 ? fmtEUR(row.tva_collectee) : '—'}
                        </td>
                        <td className={`text-right py-2 px-1 ${row.tva_deductible > 0 ? 'text-indigo-700 font-medium' : 'text-slate-400'}`}>
                          {row.tva_deductible > 0 ? fmtEUR(row.tva_deductible) : '—'}
                        </td>
                        <td className={`text-right py-2 px-1 ${row.credits_ht > 0 ? 'text-emerald-800 font-semibold' : 'text-slate-400'}`}>
                          {row.credits_ht > 0 ? fmtEUR(row.credits_ht) : '—'}
                        </td>
                        <td className="text-right py-2 px-1 text-slate-600">{fmtEUR(row.cumul_credits)}</td>
                        <td className="text-right py-2 px-1 text-slate-600">{fmtEUR(row.cumul_debits)}</td>
                        <td className="text-center py-2 pl-1">
                          <div className="flex items-center justify-center gap-1">
                            <label
                              className="cursor-pointer text-slate-500 hover:text-emerald-600 p-1 rounded hover:bg-emerald-50"
                              title="Uploader un relevé bancaire pour ce mois"
                              data-testid={`upload-releve-${row.mois}`}
                            >
                              <Banknote className="w-4 h-4" />
                              <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files && handleUploadReleveForMonth(e.target.files[0], row.mois)} />
                            </label>
                            <label
                              className="cursor-pointer text-slate-500 hover:text-blue-600 p-1 rounded hover:bg-blue-50"
                              title="Uploader une déclaration TVA pour ce mois"
                              data-testid={`upload-tva-${row.mois}`}
                            >
                              <Receipt className="w-4 h-4" />
                              <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files && handleUploadTvaForMonth(e.target.files[0], row.mois, exerciceYear)} />
                            </label>
                            {row.releves.length > 0 && (
                              <button
                                onClick={() => downloadReleve(row.releves[0].id)}
                                className="text-slate-400 hover:text-emerald-600 p-1 rounded hover:bg-emerald-50"
                                title="Télécharger le PDF du relevé bancaire"
                                data-testid={`download-releve-${row.mois}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {row.tva_declarations.length > 0 && (
                              <button
                                onClick={() => downloadTva(row.tva_declarations[0].id)}
                                className="text-slate-400 hover:text-blue-600 p-1 rounded hover:bg-blue-50"
                                title="Télécharger le PDF de la déclaration TVA"
                                data-testid={`download-tva-${row.mois}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {row.tva_declarations.length > 0 && !row.verrouille && (
                              <button
                                onClick={() => handleDeleteTva(row.tva_declarations[0].id)}
                                className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-red-50"
                                title="Supprimer la déclaration TVA"
                                data-testid={`delete-tva-${row.mois}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[11px] text-slate-500 italic mt-3 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Les mois verrouillés (au-delà du 12 du mois suivant) restent modifiables via upload mais leurs déclarations TVA existantes ne peuvent plus être modifiées ni supprimées.
              </p>
            </div>
          )}

        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, color = 'text-slate-700', icon }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
    <p className="text-[11px] text-slate-500 flex items-center gap-1">{icon}{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
  </div>
);

const RecapCard = ({ label, value, color = 'text-slate-700', bg = 'bg-slate-50 border-slate-200', tooltip }) => (
  <div className={`border rounded-lg px-3 py-2 ${bg}`} title={tooltip}>
    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{label}</p>
    <p className={`text-sm font-bold ${color} truncate`}>{fmtEUR(value)}</p>
  </div>
);

const StatusBadge = ({ status, label = '', loading = false }) => {
  if (loading) {
    return <Loader2 className="w-4 h-4 text-amber-600 animate-spin inline-block" />;
  }
  const color = status === 'vert' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300';
  const Icon = status === 'vert' ? CheckCircle2 : Circle;
  return (
    <span className={`inline-flex items-center gap-0.5 border rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default SuiviBancairePage;
